// Utilidades de scroll OPTIMIZADAS (anti-jank). La causa de los micro-freezes
// era leer getBoundingClientRect() por frame (fuerza reflow síncrono). Aquí:
//  - revealOnEnter: reveals con IntersectionObserver (asíncrono, sin forzar layout).
//  - bindProgress: progreso 0..1 calculado desde window.scrollY con el offset y el
//    alto CACHEADOS (solo se re-mide en resize) → ningún getBoundingClientRect por
//    frame, así el parallax no thrashea el layout.

const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);

/**
 * Revela elementos (añade .is-shown) cuando entran en viewport, una sola vez.
 * @param {Element[]} els
 * @param {{onShow?: (el: Element) => void}} [opts]
 */
export function revealOnEnter(els, { onShow } = {}) {
  if (!('IntersectionObserver' in window)) {
    els.forEach((el) => { el.classList.add('is-shown'); if (onShow) onShow(el); });
    return;
  }
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (!e.isIntersecting) continue;
      e.target.classList.add('is-shown');
      if (onShow) onShow(e.target);
      io.unobserve(e.target);
    }
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });
  els.forEach((el) => io.observe(el));
}

/**
 * Llama onProgress(p) por frame mientras `section` está cerca del viewport, con
 * p = avance de la sección al pasar (0 al entrar por abajo, 1 al salir por arriba).
 * No usa getBoundingClientRect por frame: cachea offset/alto y lee scrollY.
 * @returns {{stop: () => void}}
 */
export function bindProgress(section, onProgress) {
  let top = 0;
  let height = 0;
  let vh = 0;
  const measure = () => {
    const r = section.getBoundingClientRect();
    top = r.top + window.scrollY;      // offset absoluto en el documento
    height = r.height;
    vh = window.innerHeight;
  };
  measure();
  window.addEventListener('resize', measure, { passive: true });

  let running = false;
  let raf = 0;
  const tick = () => {
    const relTop = top - window.scrollY;                 // = rect.top, sin forzar layout
    onProgress(clamp01((vh - relTop) / (vh + height)));
    if (running) raf = requestAnimationFrame(tick);
  };
  const start = () => { if (!running) { running = true; measure(); raf = requestAnimationFrame(tick); } };
  const stop = () => { running = false; cancelAnimationFrame(raf); };

  if ('IntersectionObserver' in window) {
    new IntersectionObserver(
      ([e]) => (e.isIntersecting ? start() : stop()),
      { rootMargin: '300px 0px 300px 0px' },
    ).observe(section);
  } else {
    start();
  }
  return { stop };
}
